import React, { useEffect, useState } from "react";
import classNames from "classnames";
import moment from "moment";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import { Column, Row } from "components/Layout";
import Checkbox from "components/Checkbox";
import styles from "./_charts.module.scss";


const turboThreshold = 10000;

const baseParameters = [
	'externalHumidity',
	'externalTemperature',
	'soilTemperature',
	'soilHumidity',
	'leafHumidity',
	'pluviometer',
	'radiation',
	'windSpeed',
	'atmPress',
]

/**
 * da lista parametri crea nuova lista con parametri differenza
 * @param {*} list lista parametri
 * @returns lista parametri con differenze
 */
function parameterListWithDiff(list) {
	return [...list, ...list.map(a => 'diff_' + a)]
}

const filters = {
	'main': a => parameterListWithDiff(baseParameters).includes(a.id),
	'microclimate': a => parameterListWithDiff(['externalHumidity', 'externalTemperature', 'soilTemperature', 'soilHumidity', 'radiation', 'windSpeed']).includes(a.id),
	// statistics and adversities charts
	'stats': a => parameterListWithDiff(baseParameters).includes(a.id),
}

const statTypes = ['stats', 'stats-solar']


function createAxis({ id, text, format, color, ...otherProps }) {
	let plotLines = null
	if (id.endsWith('diff')) {
		plotLines = [{
			value: 0,
			color,
			width: 1,
			zIndex: 4,
		}]
	}

	return {
		id,
		gridLineWidth: 0,
		color,
		title: {
			style: { color },
			text
		},
		labels: {
			style: { color },
			format
		},
		plotLines,
		showLastLabel: false,
		visible: true,
		...otherProps
	};
}

function axisIndexById(id) {
	return chartAxes.findIndex(a => a.id === id)
}

let chartAxes = [
	{
		id: 'axis-humidity',
		text: "Umidità'",
		format: "{value}%",
		opposite: false,
		min: 0,
		max: 100,
		color: '#000',
	},
	{
		id: 'axis-humidity-diff',
		text: "Differenza Umidità'",
		format: "{value}%",
		opposite: false,
		color: '#f00',
	},
	{
		id: 'axis-temperature',
		text: "Temperatura",
		format: "{value}°C",
		opposite: false,
		color: '#000',
	},
	{
		id: 'axis-temperature-diff',
		text: "Differenza Temperatura",
		format: "{value}°C",
		opposite: false,
		color: '#f00',
	},
	{
		id: 'axis-waterPotential',
		text: "Potenziale Idrico",
		format: "{value}cb",
		opposite: false,
		color: '#000',
		reversed: true,
		min: 0,
	},
	{
		id: 'axis-waterPotential-diff',
		text: "Differenza Potenziale Idrico",
		format: "{value}cb",
		opposite: false,
		color: '#f00',
		reversed: true,
	},
	{
		id: "axis-leafHumidity",
		text: "Bagnatura Fogliare",
		format: "{value}%",
		opposite: false,
		min: 0,
		max: 100,
		color: '#000',
	},
	{
		id: "axis-leafHumidity-diff",
		text: "Differenza Bagnatura Fogliare",
		format: "{value}%",
		opposite: false,
		color: '#f00',
	},
	{
		id: "axis-rainfall",
		text: "Pioggia",
		format: "{value}mm",
		opposite: false,
		min: 0,
		color: '#000',
	},
	{
		id: "axis-rainfall-diff",
		text: "Differenza Pioggia",
		format: "{value}mm",
		opposite: false,
		min: 0,
		color: '#f00',
	},
	{
		id: "axis-radiation",
		text: "Radiazione Solare",
		format: "{value}W/mq",
		opposite: false,
		min: 0,
		color: '#000',
	},
	{
		id: "axis-radiation-diff",
		text: "Differenza Radiazione Solare",
		format: "{value}W/mq",
		opposite: false,
		color: '#f00',
	},
	{
		id: 'axis-windSpeed',
		text: "vento",
		format: "{value} km/h",
		opposite: false,
		min: 0,
		color: '#000',
	},
	{
		id: 'axis-windSpeed-diff',
		text: "Differenza vento",
		format: "{value} km/h",
		opposite: false,
		color: '#f00',
	},
	{
		id: "axis-atmPress",
		text: "pressione",
		format: "{value} hPa",
		opposite: false,
		color: '#000',
	},
	{
		id: "axis-atmPress-diff",
		text: "Differenza pressione",
		format: "{value} hPa",
		opposite: false,
		color: '#f00',
	},
].map(axis => createAxis(axis))

// lista proprietà serie base
const chartSeriesInfo_base = [
	{
		id: "externalHumidity",
		label: "UMIDITA' ATMOSFERICA",
		series: {
			name: 'Umidità',
			type: 'spline',
			tooltip: { valueSuffix: '%' },
			yAxis: 'axis-humidity',
		},
	},
	{
		id: "externalTemperature",
		label: "TEMPERATURA ESTERNA",
		series: {
			name: 'Temp. est.',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: 'axis-temperature',
		},
	},
	{
		id: "soilTemperature",
		label: "TEMPERATURA SUOLO",
		series: {
			name: 'Temp. Suolo',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: 'axis-temperature',
		},
	},
	{
		id: "soilHumidity",
		label: "POTENZIALE IDRICO",
		series: {
			name: 'Potenziale Idrico',
			type: 'spline',
			tooltip: { valueSuffix: ' cb' },
			yAxis: 'axis-waterPotential',
		},
	},
	{
		id: "leafHumidity",
		label: "BAGNATURA FOGLIARE",
		series: {
			name: 'Umidità Fogl.',
			type: 'spline',
			tooltip: { valueSuffix: ' %' },
			yAxis: 'axis-leafHumidity',
		},
	},
	{
		id: "pluviometer",
		label: "PIOGGIA",
		series: {
			name: 'Pioggia',
			type: 'spline',
			tooltip: { valueSuffix: ' mm' },
			yAxis: 'axis-rainfall',
		},
	},
	{
		id: "radiation",
		label: "RADIAZIONE SOLARE",
		series: {
			name: 'UV',
			type: 'spline',
			tooltip: { valueSuffix: ' W/mq' },
			yAxis: 'axis-radiation',
		},
	},
	{
		id: "windSpeed",
		label: "VELOCITA' DEL VENTO",
		series: {
			name: 'vento',
			type: 'spline',
			tooltip: { valueSuffix: ' km/h' },
			yAxis: 'axis-windSpeed',
		},
	},
	{
		id: "atmPress",
		label: "PRESSIONE ATMOSFERICA",
		series: {
			name: 'pressione',
			type: 'spline',
			tooltip: { valueSuffix: ' hPa' },
			yAxis: 'axis-atmPress',
		},
	},
]

// lista proprietà serie completa
// duplica la lista base aggiungendo i parametri differenza
const chartSeriesInfo = [
	...chartSeriesInfo_base.map(a => {
		let b = Object.assign({}, a)
		b.series = Object.assign({}, a.series)
		b.series.yAxis = axisIndexById(a.series.yAxis)
		return b
	}),
	...chartSeriesInfo_base.map(a => {
		let b = Object.assign({}, a)
		b.series = Object.assign({}, a.series)
		b.id = 'diff_' + a.id
		b.label = "DIFF. " + a.label
		b.series.name = "diff. " + a.series.name
		b.series.yAxis = axisIndexById(a.series.yAxis + '-diff')
		return b
	})
]

const tags_cmp2 = [
	{
		label: '1',
		color: '#000'
	},
	{
		label: '2',
		color: '#0f0'
	},
	{
		label: 'DIFF',
		color: '#f00'
	},
]

const tags_microclima = [
	{
		label: 'RIF',
		color: '#000'
	},
	{
		label: 'A',
		color: '#0f0'
	},
	{
		label: 'B',
		color: '#00f'
	},
	{
		label: 'A-RIF',
		color: '#F0F'
	},
	{
		label: 'B-RIF',
		color: '#FA0'
	},
	{
		label: 'A-B',
		color: '#F00'
	},
]

const mapTagToSeries = (series, t, i) => {
	let s = Object.assign({}, series)
	s.series = Object.assign({}, series.series)
	s.feature = series.id
	s.featureLabel = s.label
	s.cmpIdx = i
	s.id = `${series.id}#${i}`
	s.label = `${series.label} ${t.label}`
	s.series.name = `${series.series.name} ${t.label}`
	s.series.color = t.color
	s.series.turboThreshold = turboThreshold
	return s
}

let _chartOptions = {
	legend: { enabled: false },
	title: { text: null },
	chart: { zoomType: 'xy' },
	xAxis: { type: 'datetime', gridLineDashStyle: 'dash', gridLineWidth: 1 },
	yAxis: chartAxes,
	tooltip: { shared: true },
	time: { timezone: 'Europe/Rome', useUTC: false },
};


export default function ChartsCmp({ cmpSize, data, type }) {
	const [labels, setLabels] = useState([])
	const [selectedLabels, setSelectedLabels] = useState([])
	const [tags, setTags] = useState([])
	const [chartOptions, setChartOptions] = useState(_chartOptions)

	useEffect(() => setLabels(chartSeriesInfo.filter(filters[type] || filters['main']).map(a => a.id)), [type])
	useEffect(() => setSelectedLabels(labels.length ? [labels[0]] : []), [labels])
	useEffect(updateChart, [data, selectedLabels, type, cmpSize])

	function updateChart() {
		let chartOptions = Object.assign({}, _chartOptions)
		let series = [];
		let tags = [];

		if (cmpSize === 2) {
			tags = tags_cmp2
		}
		else if (cmpSize === 3 && type === 'microclimate') {
			tags = tags_microclima
		}
		else {
			tags = new Array(cmpSize).fill(null).map((_, i) => ({ label: (i + 1).toString() }))
		}
		let chartSeries = chartSeriesInfo.map((series) => {
			return tags.map((t, i) => mapTagToSeries(series, t, i))
		}).flat()
		setTags(tags)

		chartAxes.forEach(a => a.visible = false)
		// evito che nei grafici statistici la data sia disallineata per via del fuso orario
		chartOptions.time.useUTC = statTypes.includes(type)
		chartSeries.forEach(a => {
			let serie = a.series
			serie.data = data.filter(d => d.cmpIdx === a.cmpIdx)
				.map(d => ({
					name: moment(d.receiptDate).format(statTypes.includes(type) ? "DD/MM/YYYY" : "DD/MM/YYYY HH:mm"),
					x: d.receiptDate,
					y: d[a.feature],
				})).filter(d => !!d.y || d.y === 0)
			const isVisible = selectedLabels.findIndex(sl => sl === a.feature) > -1
			serie.visible = isVisible
			if (!serie.zoneAxis) {
				serie.zoneAxis = 'x'
			}
			if (serie.zoneAxis === 'x') {
				serie.zones = [{ value: moment().valueOf() }, { dashStyle: 'dash' }]
			}
			series.push(serie)
			if (isVisible) {
				chartAxes[a.series.yAxis].visible = true
			}
		})
		chartOptions.series = series
		setChartOptions(chartOptions)
	}

	function addLabel(label) {
		setSelectedLabels([label]);
	}

	return <>
		<Row>
			<Column className={classNames(styles.box, styles.chartContainer)}>
				<HighchartsReact
					highcharts={Highcharts}
					options={chartOptions}
					immutable={true}
				/>
			</Column>
		</Row>
		<Row>
			<Column className={classNames(styles.box, styles.legendContainer)}>
				<div className={styles.legendContainer__header}>
					<div className={styles["legendContainer__header-title"]}>LEGENDA</div>
					<div className={styles["legendContainer__header-subtitle"]}>Scegli quale dato visualizzare</div>
				</div>

				<div className={styles.legendOptions}>
					{tags.map((t, i) =>
						<div className={styles.legendOptions__option} key={`${i}-legend`}>
							<div className={styles.legendOptions__label}>
								<div>
									<div className={styles.legendOptions__optionFlag}
										style={{ backgroundColor: t.color, marginRight: 15 }} />
								</div>
								{t.label}
							</div>
						</div>
					)}
					{labels.map(l =>
						<div className={styles.legendOptions__option} key={`${l}-option`}>
							<Checkbox
								color={"red"}
								onCheck={() => selectedLabels.findIndex(sl => sl === l) < 0 ? addLabel(l) : null}
								checked={selectedLabels.findIndex(sl => sl === l) !== -1}
							/>
							<div className={styles.legendOptions__label}>
								{chartSeriesInfo.find(a => a.id === l).label}
							</div>
						</div>
					)}
				</div>
			</Column>
		</Row>
	</>
}
